import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Storefront, MapPin, Users, Receipt, Coins, Gift, IdentificationBadge, QrCode } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { initials, fmtDate, pts } from "@/lib/format";
import { Button } from "@/components/ui/button";
import OneKiteQR from "@/components/OneKiteQR";
import { AdminBusinessPrograms } from "@/pages/admin/BusinessPrograms";

const statusColor = { pending: "text-amber-700 bg-amber-500/10", active: "text-emerald-700 bg-emerald-600/10", suspended: "text-destructive bg-destructive/10", rejected: "text-muted-foreground bg-black/5" };

const Stat = ({ icon: Icon, label, value }) => (
  <div className="card-surface p-4"><Icon size={20} weight="duotone" className="text-primary mb-2" /><p className="font-display font-bold text-2xl">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>
);

export default function AdminBusinessDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  const load = () => { setError(false); api.get(`/admin/businesses/${id}`).then((r) => setD(r.data)).catch(() => setError(true)); };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const act = async (action) => {
    const verb = { approve: "approved", reject: "rejected", suspend: "suspended", reactivate: "reactivated" }[action] || `${action}d`;
    try { await api.post(`/admin/businesses/${id}/${action}`); toast.success(`Business ${verb}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (error) return (
    <div className="pt-16 text-center space-y-3" data-testid="business-detail-error">
      <p className="text-muted-foreground">Couldn't load this business.</p>
      <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
    </div>
  );
  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  const b = d.business;

  return (
    <div className="space-y-6" data-testid="business-detail-page">
      <button onClick={() => nav("/admin/businesses")} data-testid="back-to-businesses" className="text-muted-foreground flex items-center gap-1 text-sm hover:text-foreground"><ArrowLeft size={16} /> Businesses</button>

      <div className="card-surface p-6 flex items-start gap-4 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-xl overflow-hidden">
          {b.logo ? <img src={b.logo} alt="" className="h-full w-full object-cover" /> : initials(b.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl">{b.name}</h1>
            <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusColor[b.status]}`}>{b.status}</span>
          </div>
          {b.onekite_name && <p className="text-primary text-sm">@{b.onekite_name}</p>}
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><IdentificationBadge size={14} /> {b.onekite_id || "—"} · {b.category} · {b.city}, {b.country} · joined {fmtDate(b.created_at)}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {b.status === "pending" && <>
            <Button size="sm" data-testid="detail-approve" onClick={() => act("approve")} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Approve</Button>
            <Button size="sm" variant="outline" data-testid="detail-reject" onClick={() => act("reject")} className="rounded-full border-black/10">Reject</Button>
          </>}
          {b.status === "active" && <Button size="sm" variant="outline" data-testid="detail-suspend" onClick={() => act("suspend")} className="rounded-full border-black/10 text-destructive">Suspend</Button>}
          {(b.status === "suspended" || b.status === "rejected") && <Button size="sm" data-testid="detail-reactivate" onClick={() => act("reactivate")} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Reactivate</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Users} label="Customers" value={d.customers} />
        <Stat icon={Receipt} label="Transactions" value={d.transaction_count} />
        <Stat icon={Coins} label="Points issued" value={pts(d.points_issued)} />
        <Stat icon={Gift} label="Points redeemed" value={pts(d.points_redeemed)} />
        <Stat icon={MapPin} label="Branches" value={d.branches.length} />
      </div>

      {d.owner && (
        <div className="card-surface p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Owner</p>
          <p className="font-medium">{d.owner.name}</p>
          <p className="text-sm text-muted-foreground">{d.owner.email || "—"} · {d.owner.phone || "—"}</p>
        </div>
      )}

      <AdminBusinessPrograms programs={d.programs} />

      {d.business_qr && (
        <div className="card-surface p-5" data-testid="business-detail-qr">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3 flex items-center gap-1"><QrCode size={14} /> Business QR</p>
          <div className="flex items-start gap-5 flex-wrap">
            <OneKiteQR value={d.business_qr.token} size={150} />
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Status: <span className="text-primary capitalize">{d.business_qr.status}</span> · v{d.business_qr.version}</p>
              <p>{d.business_qr.scan_count} scans</p>
            </div>
          </div>
          {d.branch_qrs.length > 0 && (
            <div className="mt-4 border-t border-black/8 pt-4">
              <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3">Branch QRs</p>
              <div className="flex gap-5 flex-wrap">
                {d.branch_qrs.map((bq) => (
                  <div key={bq.id} className="text-center" data-testid="business-detail-branch-qr">
                    <OneKiteQR value={bq.token} size={110} />
                    <p className="text-xs mt-1 font-medium">{bq.branch_name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2 flex items-center gap-1"><MapPin size={14} /> Branches</p>
          <div className="card-surface divide-y divide-black/5">
            {d.branches.length === 0 && <p className="text-sm text-muted-foreground p-4">No branches.</p>}
            {d.branches.map((br) => (
              <div key={br.id} className="p-3.5 flex items-center justify-between" data-testid="detail-branch">
                <div><p className="font-medium text-sm">{br.name}{br.is_main ? " · Main" : ""}</p><p className="text-xs text-muted-foreground">{br.address || "—"}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2 flex items-center gap-1"><Users size={14} /> Staff</p>
          <div className="card-surface divide-y divide-black/5">
            {d.staff.length === 0 && <p className="text-sm text-muted-foreground p-4">No staff.</p>}
            {d.staff.map((s) => (
              <div key={s.id} className="p-3.5 flex items-center justify-between" data-testid="detail-staff">
                <p className="font-medium text-sm">{s.name}</p>
                <span className="text-xs text-muted-foreground capitalize">{(s.role || "").replace("business_", "")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
